import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { getTagColor } from '../utils';

function TeamAdmin({ user }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberShifts, setMemberShifts] = useState(null);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/team-admin/members');
      setMembers(res.data);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMemberShifts = async (memberId, season = null) => {
    setLoadingShifts(true);
    try {
      const url = season
        ? `/team-admin/members/${memberId}/shifts?season=${season}`
        : `/team-admin/members/${memberId}/shifts`;
      const res = await api.get(url);
      setMemberShifts(res.data);
      setAvailableSeasons(res.data.available_seasons || []);
      if (!selectedSeason && res.data.season) {
        setSelectedSeason(res.data.season);
      }
    } catch (error) {
      console.error('Error loading member shifts:', error);
    } finally {
      setLoadingShifts(false);
    }
  };

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setSelectedSeason(null);
    loadMemberShifts(member.id);
  };

  const handleSeasonChange = (season) => {
    setSelectedSeason(season);
    if (selectedMember) {
      loadMemberShifts(selectedMember.id, season);
    }
  };

  const handleBack = () => {
    setSelectedMember(null);
    setMemberShifts(null);
    setSelectedSeason(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} Min.`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Lädt...</div>;
  }

  // Detail view for selected member
  if (selectedMember && memberShifts) {
    const { summary, past_shifts, upcoming_shifts } = memberShifts;

    return (
      <div>
        <button
          onClick={handleBack}
          style={{ marginBottom: '1rem', background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontSize: '1rem' }}
        >
          ← Zurück zur Übersicht
        </button>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ marginBottom: '0.5rem' }}>
            {memberShifts.user.first_name} {memberShifts.user.last_name}
          </h2>
          <p style={{ color: '#666', margin: '0.25rem 0' }}>{memberShifts.user.email}</p>

          {memberShifts.user.helferpool && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.5rem' }}>
              {memberShifts.user.helferpool.split(/;\s*/).map((tag, idx) => {
                const trimmedTag = tag.trim();
                if (!trimmedTag) return null;
                const color = getTagColor(trimmedTag);
                return (
                  <span key={idx} style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    backgroundColor: color.bg,
                    color: color.text
                  }}>
                    {trimmedTag}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>Einsätze</h3>
          {availableSeasons.length > 0 && (
            <select
              value={selectedSeason || ''}
              onChange={(e) => handleSeasonChange(e.target.value)}
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '0.9rem'
              }}
            >
              {availableSeasons.map(season => (
                <option key={season} value={season}>
                  Saison {season}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div className="card" style={{ textAlign: 'center', backgroundColor: '#e3f2fd', padding: '1rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1565c0' }}>
              {summary.total_shifts}
            </div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Total</div>
          </div>
          <div className="card" style={{ textAlign: 'center', backgroundColor: '#e8f5e9', padding: '1rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2e7d32' }}>
              {summary.past_shifts_count}
            </div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Geleistet</div>
          </div>
          <div className="card" style={{ textAlign: 'center', backgroundColor: '#fff3e0', padding: '1rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef6c00' }}>
              {summary.upcoming_shifts_count}
            </div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Anstehend</div>
          </div>
          <div className="card" style={{ textAlign: 'center', backgroundColor: '#fce4ec', padding: '1rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c2185b' }}>
              {summary.total_hours}h
            </div>
            <div style={{ color: '#666', fontSize: '0.85rem' }}>Stunden</div>
          </div>
        </div>

        {/* Upcoming Shifts */}
        {upcoming_shifts.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem', color: '#ef6c00' }}>
              Anstehende Einsätze ({upcoming_shifts.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {upcoming_shifts.map((shift) => (
                <Link
                  key={`${shift.event_id}-${shift.shift_id}`}
                  to={`/event/${shift.event_id}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#fff3e0',
                    borderRadius: '6px',
                    border: '1px solid #ffcc80'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <strong>{shift.event_title}</strong>
                        <div style={{ color: '#666', fontSize: '0.85rem' }}>{formatDate(shift.event_date)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.85rem', color: '#ef6c00', fontWeight: '500' }}>{shift.shift_name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>{shift.start_time} Uhr</div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Past Shifts */}
        {past_shifts.length > 0 && (
          <div className="card">
            <h4 style={{ marginBottom: '0.75rem', color: '#2e7d32' }}>
              Geleistete Einsätze ({past_shifts.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {past_shifts.map((shift) => (
                <div
                  key={`${shift.event_id}-${shift.shift_id}`}
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '6px',
                    borderLeft: '3px solid #4caf50'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <strong>{shift.event_title}</strong>
                      <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                        - {shift.shift_name}
                      </span>
                      <div style={{ color: '#888', fontSize: '0.8rem' }}>{formatDate(shift.event_date)}</div>
                    </div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: '#2e7d32',
                      fontWeight: '500'
                    }}>
                      {formatDuration(shift.duration_minutes)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {upcoming_shifts.length === 0 && past_shifts.length === 0 && (
          <div className="card" style={{ textAlign: 'center', color: '#666' }}>
            Keine Einsätze in dieser Saison.
          </div>
        )}
      </div>
    );
  }

  // Member list view
  return (
    <div>
      <h2 style={{ marginBottom: '1.5rem' }}>Team Admin</h2>

      {user?.team && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Mein Team</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {user.team.split(/;\s*/).map((tag, idx) => {
              const trimmedTag = tag.trim();
              if (!trimmedTag) return null;
              const color = getTagColor(trimmedTag);
              return (
                <span key={idx} style={{
                  display: 'inline-block',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  backgroundColor: color.bg,
                  color: color.text
                }}>
                  {trimmedTag}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>
          Team-Mitglieder ({members.length})
        </h3>

        {members.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center' }}>
            Keine Team-Mitglieder gefunden.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', whiteSpace: 'nowrap' }}>Einsätze (Saison)</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'left' }}>Helferpool</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr
                    key={member.id}
                    onClick={() => handleMemberClick(member)}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ fontWeight: '500' }}>
                        {member.first_name} {member.last_name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        {member.email}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        backgroundColor: member.season_shifts_count > 0 ? '#e8f5e9' : '#f5f5f5',
                        color: member.season_shifts_count > 0 ? '#2e7d32' : '#999',
                        borderRadius: '12px',
                        fontWeight: '500'
                      }}>
                        {member.season_shifts_count}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      {member.helferpool ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {member.helferpool.split(/;\s*/).map((tag, idx) => {
                            const trimmedTag = tag.trim();
                            if (!trimmedTag) return null;
                            const color = getTagColor(trimmedTag);
                            return (
                              <span key={idx} style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '10px',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                backgroundColor: color.bg,
                                color: color.text,
                                whiteSpace: 'nowrap'
                              }}>
                                {trimmedTag}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamAdmin;
